import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Sparkles, Loader2, Send, Save, Settings, Mail, Check } from 'lucide-react';
import { ai, AI_PROXY_CHAT_MODEL } from '../services/geminiService';
import {
    createDraftMessage,
    sendDirectMessage,
    getMailboxSignature,
    verifyMicrosoftGraphMailSession,
} from '../services/microsoftGraphService';
import { supabase, supabase_save_candidate_communication } from '../services/supabaseService';
import { RichTextEditor } from './RichTextEditor';

export interface BulkCandidate {
    id: string;
    profile_id?: string;
    name: string;
    email: string;
    phone?: string;
    vacancyTitle?: string;
    companyName?: string;
    /** Optional fallback when no vacancy context is available (e.g. Talent Pool bulk selection). */
    role?: string;
}

interface BulkEmailComposerModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCandidates: BulkCandidate[];
  vacancyData?: any;
  initialBody?: string;
  onSuccessCallback?: () => void;
}

const BulkEmailComposerModal: React.FC<BulkEmailComposerModalProps> = ({ isOpen, onClose, selectedCandidates, vacancyData, initialBody, onSuccessCallback }) => {
  const [subject, setSubject] = useState(`Interessante vacature: ${vacancyData?.title || 'Openstaande Rol'}`);
  const [body, setBody] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [permissionsError, setPermissionsError] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [signature, setSignature] = useState<string | null>(null);
  /** null = bezig met check bij openen; true = Graph accepteert token; false = nu al niet mailen */
  const [m365MailReady, setM365MailReady] = useState<boolean | null>(null);
  const [m365MailCheckMessage, setM365MailCheckMessage] = useState('');
  
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [templates, setTemplates] = useState<any[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [sharedMailboxes, setSharedMailboxes] = useState<string[]>([]);
  const [selectedFromEmail, setSelectedFromEmail] = useState<string>('');

  /** Primitieven i.p.v. vacancyData-object: anders nieuwe object-referentie elke parent-render → eindeloze M365-check. */
  const vacancyIdForEffect = vacancyData != null && typeof vacancyData === 'object' && 'id' in vacancyData && vacancyData.id != null ? String(vacancyData.id) : '';
  const vacancyTitleForEffect =
    vacancyData != null && typeof vacancyData === 'object' && 'title' in vacancyData && vacancyData.title != null
      ? String(vacancyData.title)
      : '';
  const initialBodyForEffect = initialBody ?? '';

  useEffect(() => {
      if (!isOpen) {
          setSubject(`Interessante vacature: ${vacancyTitleForEffect || 'Openstaande Rol'}`);
          setBody('');
          setCc('');
          setBcc('');
          setSelectedTemplateId('');
          setSelectedFromEmail('');
          setPermissionsError(false);
          setAuthError(false);
          setM365MailReady(null);
          setM365MailCheckMessage('');
          return;
      }

      let cancelled = false;
      setM365MailReady(null);
      setM365MailCheckMessage('');
      void verifyMicrosoftGraphMailSession().then((r) => {
          if (cancelled) return;
          if (r.ok) {
              setM365MailReady(true);
              setM365MailCheckMessage('');
          } else {
              setM365MailReady(false);
              setM365MailCheckMessage(r.message);
          }
      });
      if (initialBodyForEffect) {
          setTimeout(() => {
              if (!cancelled) setBody(initialBodyForEffect);
          }, 100);
      }
      const fetchClientData = async () => {
          const { data: authData } = await supabase.auth.getUser();
          if (cancelled || !authData?.user) return;
          // Force loading templates from the recruiter's OWN agency settings
          // bypassing vacancyData which might have relational overrides in some cases.
          const { data: profileData } = await supabase.from('profiles').select('client_id').eq('id', authData.user.id).single();
          let targetClientId = profileData?.client_id;

          console.log('BulkEmailComposerModal: resolved targetClientId for templates =', targetClientId);
          if (!targetClientId) return;

          const { data } = await supabase.from('clients').select('data').eq('id', targetClientId).single();
          if (cancelled) return;

          if (data?.data?.email_templates) {
              setTemplates(data.data.email_templates);
          }
          if (data?.data?.shared_mailboxes) {
              const mailboxes = data.data.shared_mailboxes.split(',').map((s: string) => s.trim()).filter(Boolean);
              setSharedMailboxes(mailboxes);
          }
          if (data?.data?.default_shared_mailbox) {
              setSelectedFromEmail(data.data.default_shared_mailbox);
          }
          if (data?.data?.user_signatures && data.data.user_signatures[authData.user.id]) {
              setSignature(data.data.user_signatures[authData.user.id]);
          } else if (data?.data?.email_signature) {
              setSignature(data.data.email_signature);
          } else {
              // Fallback to Microsoft Signature
              getMailboxSignature().then((sig) => {
                  if (!cancelled && sig) setSignature(sig);
              });
          }
      };
      void fetchClientData();

      return () => {
          cancelled = true;
      };
  }, [isOpen, vacancyIdForEffect, vacancyTitleForEffect, initialBodyForEffect]);

  const handleTemplateSelect = (tplId: string) => {
      setSelectedTemplateId(tplId);
      const tpl = templates.find(t => t.id === tplId);
      if (tpl) {
          setSubject(tpl.subject || '');
          setBody(tpl.body || '');
      } else if (tplId === '') {
          setBody('');
      }
  };

  const handleMicrosoftReLogin = async () => {
      try {
          await supabase.auth.signOut();
      } catch (e) {
          console.warn('signOut before Microsoft re-login', e);
      }
      try {
          const { data, error } = await supabase.auth.signInWithOAuth({
              provider: 'azure',
              options: {
                  redirectTo: `${window.location.origin}/`,
                  skipBrowserRedirect: true,
                  scopes:
                      'Mail.Send MailboxSettings.Read Calendars.ReadWrite Files.ReadWrite User.Read email profile',
                  queryParams: { prompt: 'select_account' },
              } as any,
          });
          if (error) throw error;
          if (data?.url) window.location.href = data.url;
          else throw new Error('Geen redirect-URL van authenticatieserver.');
      } catch (e) {
          console.error('Microsoft re-login OAuth', e);
      }
  };

  if (!isOpen) return null;

  const validCandidates = selectedCandidates.filter(c => c.email);

  const handleGenerateAI = async () => {
    setIsGenerating(true);
    try {
        const { data: userData } = await supabase.auth.getUser();
        const recruiterName = userData?.user?.user_metadata?.name || 'Recruiter';

        const prompt = `Je bent een ervaren en overtuigende recruitment copywriter. Je schrijft namens de recruiter (${recruiterName}).
Schrijf een wervende, doeltreffende recruitment e-mail om kandidaten te interesseren.

${body.replace(/<[^>]*>?/gm, '').trim() ? `De recruiter heeft al het volgende concept of instructies geschreven. Verbeter dit bericht of gebruik deze instructies als basis:\n"""\n${body.replace(/<[^>]*>?/gm, '').trim()}\n"""` : ''}

Context van vacature:
${vacancyData?.title ? 'Titel: ' + vacancyData.title : ''}
${vacancyData?.description?.substring(0, 1500) || 'Geen verdere context beschikbaar.'}

Regels:
1. CRUCIAAL: Gebruik in je bericht EXACT de volgende shortcodes (inclusief blokhaken). Vul ze NIET zelf in, maar typ letterlijk de blokhaken!
   FOUT: "Beste Jan," of "voor de rol van Developer bij Google"
   GOED: "Beste [NAAM]," of "voor de rol van [VACATURE] bij [BEDRIJF]"
   - Typ altijd letterlijk: [NAAM]
   - Typ altijd letterlijk: [VACATURE]
   - Typ altijd letterlijk: [BEDRIJF]
2. Spreek namens de recruiter, dus NIET namens "Froukje". Praat in de ik-vorm als recruiter.
3. Houd het tot maximaal 3 krachtige alinea's. 
4. Gebruik strikt platte tekst met natuurlijke witregels. Gebruik HTML linebreaks (<br>) of normale newline karakters. Gebruik GEEN Markdown of dikgedrukte tekst.
5. Sluit af met een korte, professionele afscheidsgroet (bijv. "Met vriendelijke groet,"), maar schrijf GEEN NAAM eronder. De e-mailhandtekening wordt namelijk automatisch toegevoegd via de shortcode.
6. Schrijf GEEN extra toelichting of AI introductie, schrijf direct en uitsluitend de uiteindelijke e-mail.`;

        const response = await ai.models.generateContent({
            model: AI_PROXY_CHAT_MODEL,
            contents: prompt
        });

        if (response.text) {
            setBody(response.text.trim());
        }
    } catch (e: any) {
        console.error("AI Generation error:", e);
        alert("Fout tijdens AI generatie: " + e.message);
    } finally {
        setIsGenerating(false);
    }
  };

  const handleM365Send = async (asDraft: boolean = true) => {
      if (validCandidates.length === 0) {
          alert("Er zijn geen e-mailadressen geselecteerd.");
          return;
      }
      if (m365MailReady !== true) {
          alert(
              m365MailCheckMessage ||
                  'Microsoft 365 is nog niet gecontroleerd of je sessie is verlopen. Wacht op de check of log opnieuw in met Microsoft.'
          );
          return;
      }

      setIsSending(true);
      let successCount = 0;
      let failCount = 0;

      for (const cand of validCandidates) {
          const firstName = cand.name.split(' ')[0] || 'Kandidaat';
          const vacancyFallbackTitle =
              vacancyData?.title ||
              vacancyData?.vacancyTitle ||
              vacancyData?.vacancies?.title ||
              vacancyData?.data?.title ||
              vacancyData?.data?.vacancyTitle ||
              '';
          const companyFallbackName =
              vacancyData?.companyName ||
              vacancyData?.clientName ||
              vacancyData?.client?.name ||
              vacancyData?.clients?.name ||
              vacancyData?.source_client?.name ||
              vacancyData?.data?.clientName ||
              '';

          let clientName = cand.companyName || companyFallbackName || 'ons bedrijf';
          let vacancyTitle = cand.vacancyTitle || vacancyFallbackTitle || cand.role || 'deze functie';

          let personalizedSubject = subject.replace(/\[Naam\]/gi, firstName)
                                           .replace(/\[Voornaam\]/gi, firstName)
                                           .replace(/\[Achternaam\]/gi, cand.name.split(' ').slice(1).join(' ') || '')
                                           .replace(/\[Functie\]/gi, vacancyTitle)
                                           .replace(/\[Bedrijf\]/gi, clientName)
                                           .replace(/\[Bedrij\]/gi, clientName)
                                           .replace(/\[Vacature\]/gi, vacancyTitle);

          let personalizedBody = body.replace(/\[Naam\]/gi, firstName)
                                     .replace(/\[Voornaam\]/gi, firstName)
                                     .replace(/\[Achternaam\]/gi, cand.name.split(' ').slice(1).join(' ') || '')
                                     .replace(/\[Functie\]/gi, vacancyTitle)
                                     .replace(/\[Bedrijf\]/gi, clientName)
                                     .replace(/\[Bedrij\]/gi, clientName)
                                     .replace(/\[Vacature\]/gi, vacancyTitle);
                                     
          // Fix Outlook double spacing by converting <p> to <div>
          personalizedBody = personalizedBody.replace(/<p\b[^>]*>/gi, '<div style="margin: 0; padding: 0;">').replace(/<\/p>/gi, '</div>');
          
          const isSignatureInBody = signature && personalizedBody.replace(/\s/g, '').includes(signature.replace(/\s/g, '').substring(0, 20));
          
          if (personalizedBody.match(/\[HANDTEKENING\]/i)) {
              personalizedBody = personalizedBody.replace(/\[HANDTEKENING\]/gi, signature || '');
          } else if (signature && !isSignatureInBody) {
              personalizedBody += `<br><br>${signature}`;
          }

          const ccArr = cc.split(',').map(s => s.trim()).filter(Boolean);
          const bccArr = bcc.split(',').map(s => s.trim()).filter(Boolean);

          let logSuccess = false;
          let reqError: any = null;
          if (asDraft) {
              const { success, error } = await createDraftMessage(personalizedSubject, personalizedBody, cand.email, ccArr, bccArr, selectedFromEmail || undefined);
              if (success) { successCount++; logSuccess = true; }
              else { failCount++; reqError = error; }
          } else {
              const { success, error } = await sendDirectMessage(personalizedSubject, personalizedBody, cand.email, ccArr, bccArr, selectedFromEmail || undefined);
              if (success) { successCount++; logSuccess = true; }
              else { failCount++; reqError = error; }
          }

          if (reqError?.message?.includes('does not have the right to send mail')) {
              setPermissionsError(true);
          }

          if (logSuccess) {
               try {
                   const { data: userData } = await supabase.auth.getUser();
                   if (userData?.user) {
                       const plainBody = personalizedBody.replace(/<br>/g, '\n').replace(/<[^>]*>?/gm, '');
                       const actionVerb = asDraft ? 'Concept aangemaakt' : 'E-mail verstuurd';
                       const typeVerb = asDraft ? 'CONCEPT_AANGEMAAKT' : 'EMAIL_VERSTUURD';
                       
                       await supabase_save_candidate_communication(
                           cand.profile_id || cand.id,
                           userData.user.id,
                           `${actionVerb} via M365:\nOnderwerp: ${personalizedSubject}\n\n${plainBody}`,
                           'email'
                       );
                   }
               } catch(e) {
                   console.error("Log failed", e);
               }
          }
      }

      setIsSending(false);
      
      if (successCount > 0) {
          if (asDraft) {
              setSuccessMessage(`Er zijn ${successCount} concept e-mails succesvol aangemaakt in je Outlook map 'Concepten'.`);
          } else {
              setSuccessMessage(`Er zijn ${successCount} e-mails succesvol verstuurd!`);
          }
      } else if (failCount > 0) {
          setAuthError(true);
      }
  };

  return createPortal(
    <div className="fixed inset-0 z-[500] flex justify-end bg-slate-900/40 backdrop-blur-sm animate-fadeIn" onClick={onClose}>
      <div 
        className="bg-white shadow-[0_0_40px_rgba(0,0,0,0.1)] w-[850px] max-w-full h-full flex flex-col transform transition-transform duration-300 translate-x-0 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        
        {/* Header */}
        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-[#185ABD]/5 shrink-0">
          <div className="flex items-center gap-3">
             <div className="w-10 h-10 bg-[#185ABD]/10 rounded-full flex items-center justify-center shadow-inner text-[#185ABD]">
                <Mail size={18} />
             </div>
             <div>
                <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight flex items-center gap-3">
                  M365 Bulk E-mail Studio
                </h2>
                <p className="text-xs font-semibold text-slate-500 mt-0.5 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                  {validCandidates.length} kandidaten geselecteerd (Aparte concepten)
                </p>
             </div>
          </div>
          <button onClick={onClose} className="p-2 bg-white hover:bg-slate-100 text-slate-400 hover:text-slate-600 rounded-full transition-colors border border-slate-200">
            <X size={20} />
          </button>
        </div>

        {/* Form Body & Sidebar Container */}
        <div className="flex-1 flex overflow-hidden">
          {/* Form Body */}
          <div className="p-6 space-y-5 overflow-y-auto custom-scrollbar flex-1 bg-slate-50">
           
           {successMessage ? (
               <div className="bg-white border border-slate-200 rounded-xl p-8 flex flex-col items-center justify-center text-center shadow-sm min-h-[300px]">
                   <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mb-4">
                       <Check size={32} />
                   </div>
                   <h3 className="text-xl font-black text-slate-800 mb-2">Gelukt!</h3>
                   <p className="text-sm font-medium text-slate-500 mb-6 max-w-sm leading-relaxed">
                       {successMessage}
                   </p>
                   <button 
                       onClick={() => {
                           if (onSuccessCallback) onSuccessCallback();
                           onClose();
                       }}
                       className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-2.5 rounded-xl text-sm font-bold transition-all shadow-sm active:scale-95"
                   >
                       Sluiten
                   </button>
               </div>
           ) : (
             <>
               {m365MailReady === null && (
                   <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex flex-col items-start gap-3 shadow-sm mb-6">
                       <div className="flex items-center gap-2 text-amber-800 font-black uppercase tracking-widest text-[10px]">
                           <Loader2 size={14} className="animate-spin shrink-0" />
                           <span>Microsoft 365 wordt gecontroleerd</span>
                       </div>
                       <p className="text-sm font-medium text-amber-900 leading-relaxed">
                           We testen of je nog mail mag sturen vanuit Outlook. Zo voorkom je dat je eerst alles opstelt en pas daarna een verlopen sessie ziet.
                       </p>
                   </div>
               )}
               {m365MailReady === false && (
                   <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex flex-col items-start gap-3 shadow-sm mb-6">
                       <div className="flex items-center gap-2 text-red-600 font-black uppercase tracking-widest text-[10px]">
                           <span>Nu geen mail vanuit M365 mogelijk</span>
                       </div>
                       <p className="text-sm font-medium text-red-800 leading-relaxed">{m365MailCheckMessage}</p>
                       <div className="flex flex-wrap gap-2">
                           <button
                               type="button"
                               onClick={() => {
                                   setM365MailReady(null);
                                   setM365MailCheckMessage('');
                                   void verifyMicrosoftGraphMailSession().then((r) => {
                                       if (r.ok) {
                                           setM365MailReady(true);
                                           setM365MailCheckMessage('');
                                       } else {
                                           setM365MailReady(false);
                                           setM365MailCheckMessage(r.message);
                                       }
                                   });
                               }}
                               className="bg-white border border-red-200 hover:bg-red-100/60 text-red-800 px-4 py-2 rounded-lg text-xs font-bold transition-colors shadow-sm"
                           >
                               Opnieuw controleren
                           </button>
                           <button
                               type="button"
                               onClick={() => void handleMicrosoftReLogin()}
                               className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-xs font-bold transition-colors shadow-sm"
                           >
                               Opnieuw inloggen met Microsoft
                           </button>
                       </div>
                   </div>
               )}
               {permissionsError && (
                   <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex flex-col items-start gap-3 shadow-sm mb-6">
                       <div className="flex items-center gap-2 text-orange-600 font-black uppercase tracking-widest text-[10px]">
                           <span>Geen "Verzenden Als" Rechten</span>
                       </div>
                       <p className="text-sm font-medium text-orange-800 leading-relaxed">
                           Je probeert te mailen vanuit de mailbox <strong>{selectedFromEmail}</strong>, maar jouw eigen Microsoft 365 account heeft hiervoor geen <strong>"Verzenden Als" (Send As)</strong> of <strong>"Verzenden namens"</strong> rechten gekregen in het Exchange Admin Center. Vraag je IT-beheerder om je deze rechten te geven, of kies je eigen mailbox.
                       </p>
                   </div>
               )}
               {authError && !permissionsError && (
                   <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex flex-col items-start gap-3 shadow-sm mb-6">
                       <div className="flex items-center gap-2 text-red-600 font-black uppercase tracking-widest text-[10px]">
                           <span>M365 Sessie Verlopen / Fout</span>
                       </div>
                       <p className="text-sm font-medium text-red-800 leading-relaxed">
                           We konden geen verbinding maken met Microsoft 365. Waarschijnlijk is je sessie verlopen. Log opnieuw in om e-mails te versturen.
                       </p>
                       <button 
                           type="button"
                           onClick={() => void handleMicrosoftReLogin()}
                           className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-xs font-bold transition-colors shadow-sm"
                       >
                           Opnieuw inloggen met Microsoft
                       </button>
                   </div>
               )}

               <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm space-y-4">
                   <div className="flex items-center gap-3 pb-2 border-slate-100">
                       <div className="flex-1">
                           <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
                              Kies een Template
                           </label>
                           {templates.length > 0 ? (
                               <select
                                   value={selectedTemplateId}
                                   onChange={(e) => handleTemplateSelect(e.target.value)}
                                   className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-bold text-slate-800 outline-none focus:ring-2 focus:ring-indigo-500 transition-all cursor-pointer"
                               >
                                   <option value="">-- Geen template geselecteerd --</option>
                                   
                                   {templates.filter(t => {
                                       const s = (t as any).pipelineStatuses || ((t as any).pipelineStatus ? [(t as any).pipelineStatus] : []);
                                       return s.length === 0;
                                   }).map(tpl => (
                                       <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                                   ))}

                                   {['Shortlist', 'Voorgesteld', 'Gesprek', 'Aanbod', 'Plaatsing', 'Rejected'].map(status => {
                                       const matchingTemplates = templates.filter(t => {
                                           const s = (t as any).pipelineStatuses || ((t as any).pipelineStatus ? [(t as any).pipelineStatus] : []);
                                           return s.includes(status);
                                       });
                                       if (matchingTemplates.length === 0) return null;
                                       return (
                                           <optgroup key={status} label={`Fase: ${status === 'Rejected' ? 'Afgewezen' : status}`}>
                                               {matchingTemplates.map(tpl => (
                                                   <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                                               ))}
                                           </optgroup>
                                       );
                                   })}
                               </select>
                           ) : (
                               <div className="w-full px-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-lg text-sm font-medium text-slate-500 italic">
                                   Er zijn nog geen templates ingesteld.
                               </div>
                           )}
                       </div>
                       <div className="pt-6">
                           <button 
                               onClick={() => {
                                   localStorage.setItem('settings_initial_tab', 'email_templates');
                                   window.dispatchEvent(new CustomEvent('navigate-tab', { detail: 'settings' }));
                                   onClose();
                               }}
                               className="px-3 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg text-xs font-bold transition-all flex items-center gap-2 shrink-0"
                           >
                               <Settings size={14} /> Beheren
                           </button>
                       </div>
                   </div>

               <div className="grid grid-cols-2 gap-4">
                   <div>
                       <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5">
                          CC (gescheiden door komma)
                       </label>
                       <input 
                          type="text" 
                          value={cc}
                          onChange={(e) => setCc(e.target.value)}
                          className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[11px] font-medium shadow-none focus:ring-1 focus:ring-indigo-500 outline-none placeholder:text-slate-300"
                          placeholder="Bijv. collega@bedrijf.nl"
                       />
                   </div>
                   <div>
                       <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5">
                          BCC
                       </label>
                       <input 
                          type="text" 
                          value={bcc}
                          onChange={(e) => setBcc(e.target.value)}
                          className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[11px] font-medium shadow-none focus:ring-1 focus:ring-indigo-500 outline-none placeholder:text-slate-300"
                          placeholder="Bijv. archief@bedrijf.nl"
                       />
                   </div>
               </div>

               {sharedMailboxes.length > 0 && (
               <div>
                   <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5">
                      Verzenden Als (Afzender)
                   </label>
                   <select 
                      value={selectedFromEmail}
                      onChange={(e) => setSelectedFromEmail(e.target.value)}
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-bold shadow-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-all cursor-pointer"
                   >
                       <option value="">-- Mijn eigen mailbox --</option>
                       {sharedMailboxes.map(mb => (
                           <option key={mb} value={mb}>{mb}</option>
                       ))}
                   </select>
               </div>
               )}

               <div>
                   <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
                      Onderwerp
                   </label>
                   <input 
                      type="text" 
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-bold shadow-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                      placeholder="Typ een wervend onderwerp..."
                   />
               </div>
           </div>

           <div>
               <div className="flex items-center justify-between mb-2">
                   <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 flex flex-wrap items-center gap-1">
                      Bericht inhoud 
                      <span className="text-[9px] font-bold text-slate-400 ml-2 normal-case flex flex-wrap gap-1 items-center">
                          Variabelen: 
                          {['[NAAM]', '[FUNCTIE]', '[BEDRIJF]', '[VACATURE]', '[HANDTEKENING]'].map(sc => (
                              <button key={sc} onClick={() => setBody(prev => prev + sc)} className="bg-white hover:bg-indigo-50 px-1 py-0.5 rounded text-indigo-600 border border-transparent hover:border-indigo-100 transition-colors shadow-sm">{sc}</button>
                          ))}
                      </span>
                   </label>
                   <div className="flex gap-2">
                       <button 
                           onClick={() => setBody(prev => prev + `<br><br>${signature}`)}
                           disabled={!signature}
                           className="text-xs font-bold text-slate-600 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
                       >
                           Voeg Handtekening Toe
                       </button>
                       <button 
                           onClick={handleGenerateAI}
                           disabled={isGenerating}
                           className="text-xs font-bold text-indigo-600 hover:text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
                       >
                           {isGenerating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                           Froukje, schrijf dit voor mij
                       </button>
                   </div>
               </div>
               <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                   <RichTextEditor 
                      value={body}
                      onChange={(val) => setBody(val)}
                      placeholder="Schrijf hier je e-mail, of laat Froukje AI een concept maken..."
                   />
               </div>
               
               {signature && (
                   <div className="mt-4 pt-4 border-t border-slate-200">
                       <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
                          M365 Handtekening (Wordt automatisch toegevoegd)
                       </label>
                       <div 
                           className="text-xs bg-white border border-slate-200 p-4 rounded-xl opacity-70 cursor-not-allowed max-h-32 overflow-y-auto custom-scrollbar"
                           dangerouslySetInnerHTML={{ __html: signature }} 
                       />
                   </div>
               )}
               <p className="text-[10px] font-semibold text-slate-400 mt-2 uppercase tracking-widest">
                   Let op: Deze functie maakt voor élke geselecteerde kandidaat een aparte, gepersonaliseerde Concept e-mail aan in je Microsoft 365 Outlook. [Naam] wordt automatisch vervangen door de voornaam.
               </p>
           </div>
           </>
           )}
          </div>

          {/* Right Sidebar - Candidates List */}
          <div className="w-72 bg-white border-l border-slate-100 flex flex-col">
             <div className="p-5 border-b border-slate-100">
                <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center justify-between">
                    Kandidaten Lijst <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-[9px]">{validCandidates.length}</span>
                </h4>
             </div>
             <ul className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-2">
                 {selectedCandidates.map((c, index) => (
                     <li key={`${c.id}-${index}`} className="flex flex-col p-2 hover:bg-slate-50 rounded-lg transition-colors">
                         <div className="flex items-center gap-2 mb-1">
                             <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${c.email ? 'bg-emerald-400' : 'bg-red-400'}`}></div>
                             <span className={`text-xs font-bold truncate ${!c.email ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                                 {c.name}
                             </span>
                         </div>
                         {c.email && (
                             <span className="text-[10px] text-slate-500 ml-3.5 truncate w-full" title={c.email}>
                                 {c.email}
                             </span>
                         )}
                     </li>
                 ))}
             </ul>
             {selectedCandidates.length !== validCandidates.length && (
                 <div className="p-4 border-t border-slate-100 bg-red-50">
                     <p className="text-[10px] text-red-500 font-bold leading-relaxed">
                         {selectedCandidates.length - validCandidates.length} geselecteerde {selectedCandidates.length - validCandidates.length === 1 ? 'kandidaat heeft' : 'kandidaten hebben'} geen geldig e-mailadres en {selectedCandidates.length - validCandidates.length === 1 ? 'wordt' : 'worden'} overgeslagen.
                     </p>
                 </div>
             )}
          </div>
        </div>

        {/* Footer Actions */}
        {!successMessage && (
        <div className="px-8 py-6 border-t border-slate-100 flex justify-end gap-3 bg-white shrink-0">
           <button 
              onClick={onClose}
              disabled={isSending}
              className="px-6 py-3 text-sm font-bold text-slate-500 hover:bg-slate-50 rounded-xl transition-colors disabled:opacity-50"
           >
              Annuleren
           </button>
           <button 
              onClick={() => handleM365Send(true)}
              disabled={
                  validCandidates.length === 0 ||
                  !body.trim() ||
                  isSending ||
                  m365MailReady !== true
              }
              className="px-6 py-3 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-bold rounded-xl transition-all shadow-sm flex items-center gap-2 disabled:opacity-50"
           >
              {isSending ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} 
              Concept Opslaan
           </button>
           <button 
              onClick={() => handleM365Send(false)}
              disabled={
                  validCandidates.length === 0 ||
                  !body.trim() ||
                  isSending ||
                  m365MailReady !== true
              }
              className="px-6 py-3 bg-[#0078D4] hover:bg-[#0063B1] text-white text-sm font-bold rounded-xl transition-all shadow-md shadow-[#0078D4]/20 flex items-center gap-2 disabled:opacity-50"
           >
              {isSending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} 
              Direct Verzenden
           </button>
        </div>
        )}

      </div>
    </div>,
    document.body
  );
};

export default BulkEmailComposerModal;
